import { SelectionPackaged } from "@screencloud/screencloud-ui-components";
import { useEffect } from "react";
import {
  FormattedMessage,
  WrappedComponentProps,
  injectIntl,
} from "react-intl";
import { useAppContext } from "src/hooks/useAppContext";
import {
  OnboardingPlanSelectionType,
  trackOnboardingPlanSelectionEvent,
} from "src/utils/mixpanel";
import type { StepProps } from "../../hooks/usePixiWizardState";
import {
  isOtherShippingCountry,
  resolveDefaultShippingCountry,
} from "../../shippingCountries";
import { ExclusiveDeals } from "./ExclusiveDeals";
import { PlanCard } from "./PlanCard";
import {
  BillingCycle,
  DealId,
  dealEligibility,
  DEFAULT_BILLING_CYCLE,
  DEFAULT_PLAN,
  PLAN_CONTENT,
  PlanId,
} from "./constants";
import {
  isFreeDeviceAvailable,
  resolveEffectiveDealId,
  resolveSelection,
} from "./selection";
import { useOnboardingPlansContext } from "./useOnboardingPlans";
import {
  CardsRow,
  ChoiceColumn,
  ChoiceHeader,
  ChoiceTitle,
  PlanStateMessage,
  SelectorsRow,
  StepLayout,
} from "./styles";

const PlanBillingStepComponent = ({
  state,
  updateState,
  intl,
}: StepProps & WrappedComponentProps) => {
  const context = useAppContext();
  const { loading, error, getPlan, isProTrialist, orgCountryCode } =
    useOnboardingPlansContext();

  const { planId, currency, billingCycle, shippingCountry, licenses } =
    resolveSelection(state, isProTrialist);

  // The valid deal for the current selection — free-device is stripped (→
  // discount, or nothing) when the "Ship to" country is unsupported (`Other`).
  // Everything below (card pricing, deal highlight) derives from this, never the
  // raw persisted dealId (GRWBKK-1730).
  const effectiveDealId = resolveEffectiveDealId(state);

  // Persist the default selection on mount so the highlighted card and the
  // Continue gate agree (Pro-trialists are pinned to Pro). The "Ship to" country
  // defaults to the Studio org country when available (falling back to US); the
  // default deal is free-device when it can ship there, otherwise the discount.
  useEffect(() => {
    const patch: Partial<typeof state> = {};
    const nextPlan = isProTrialist ? "pro" : (state.planId ?? DEFAULT_PLAN);
    const nextCycle = state.billingCycle ?? DEFAULT_BILLING_CYCLE;
    const nextCountry =
      state.shippingCountry ?? resolveDefaultShippingCountry(orgCountryCode);
    if (!state.planId) patch.planId = nextPlan;
    if (!state.billingCycle) patch.billingCycle = nextCycle;
    if (!state.shippingCountry) patch.shippingCountry = nextCountry;
    if (!state.dealId && dealEligibility(nextCycle).anyDeal) {
      patch.dealId = isFreeDeviceAvailable(nextCycle, nextCountry)
        ? "free-device"
        : "discount";
    }
    if (Object.keys(patch).length) updateState(patch);
  }, []);

  // Keep the persisted deal valid as the selection changes: when the user picks
  // `Other` (or an unsupported org default resolves to it) a stale `free-device`
  // is replaced with the discount deal so it is never carried forward as the
  // active deal (GRWBKK-1730). Idempotent — settles after one pass.
  useEffect(() => {
    if (effectiveDealId !== state.dealId) {
      updateState({ dealId: effectiveDealId });
    }
  }, [effectiveDealId, state.dealId, updateState]);

  const trackSelection = ({
    selectionType,
    selectionValue,
    nextPlanId = planId,
    nextBillingCycle = billingCycle,
    nextDealId = effectiveDealId,
  }: {
    selectionType: OnboardingPlanSelectionType;
    selectionValue: string;
    nextPlanId?: PlanId;
    nextBillingCycle?: BillingCycle;
    nextDealId?: DealId;
  }) => {
    trackOnboardingPlanSelectionEvent({
      context,
      status: "selected",
      planTier: nextPlanId,
      billingCycle: nextBillingCycle,
      deal: nextDealId,
      licenses,
      shippingCountry,
      selectionType,
      selectionValue,
    });
  };

  // Both plans offer the same deals now, so switching plan keeps the deal.
  const selectPlan = (next: PlanId) => {
    if (next === planId) return;
    updateState({ planId: next });
    trackSelection({
      selectionType: "plan",
      selectionValue: next,
      nextPlanId: next,
    });
  };

  const selectBillingCycle = (cycle: BillingCycle) => {
    const requestedDealId =
      cycle === "month" ? undefined : (state.dealId ?? "free-device");
    const nextDealId = resolveEffectiveDealId({
      ...state,
      billingCycle: cycle,
      dealId: requestedDealId,
    });
    updateState({
      billingCycle: cycle,
      // Monthly has no deals — drop any deal; Yearly defaults to free-device.
      dealId: requestedDealId,
    });
    trackSelection({
      selectionType: "billing_cycle",
      selectionValue: cycle,
      nextBillingCycle: cycle,
      nextDealId,
    });
  };

  // Pro-trialist: each card is a cycle; selecting it sets pro + that cycle.
  const selectProCycle = (cycle: BillingCycle) => {
    const requestedDealId =
      cycle === "month" ? undefined : (state.dealId ?? "free-device");
    const nextDealId = resolveEffectiveDealId({
      ...state,
      planId: "pro",
      billingCycle: cycle,
      dealId: requestedDealId,
    });
    updateState({
      planId: "pro",
      billingCycle: cycle,
      dealId: requestedDealId,
    });
    trackSelection({
      selectionType: "billing_cycle",
      selectionValue: cycle,
      nextPlanId: "pro",
      nextBillingCycle: cycle,
      nextDealId,
    });
  };

  const billingOptions = [
    {
      label: intl.formatMessage({
        id: "pixi_wizard.plan.billing.yearly",
        defaultMessage: "Yearly",
      }),
      value: "year",
    },
    {
      label: intl.formatMessage({
        id: "pixi_wizard.plan.billing.monthly",
        defaultMessage: "Monthly",
      }),
      value: "month",
    },
  ];

  const monthlySuffix = intl.formatMessage({
    id: "pixi_wizard.plan.cycle.monthly",
    defaultMessage: "Monthly",
  });
  const annualSuffix = intl.formatMessage({
    id: "pixi_wizard.plan.cycle.annual",
    defaultMessage: "Annual",
  });

  const corePlan = getPlan("core", billingCycle, currency);
  const proPlan = getPlan("pro", billingCycle, currency);
  const coreMonthly = getPlan("core", "month", currency);
  const proMonthly = getPlan("pro", "month", currency);
  const proAnnual = getPlan("pro", "year", currency);

  // Yearly + free-device shows the monthly rate instead of yearly/12 — the free
  // Pixi replaces the annual discount (GRWBKK-1695). See `billingPeriodPriceCents`.
  // Derived from the effective deal so a stale free-device under `Other` no
  // longer keeps free-device pricing on the cards (GRWBKK-1730).
  const isFreeDevice = effectiveDealId === "free-device";

  const hasPlans = isProTrialist
    ? Boolean(proMonthly || proAnnual)
    : Boolean(corePlan || proPlan);

  const renderCards = () => {
    if (loading) {
      return (
        <PlanStateMessage data-testid="plan-billing-loading">
          <FormattedMessage
            id="pixi_wizard.plan.loading"
            defaultMessage="Loading plans…"
          />
        </PlanStateMessage>
      );
    }
    if (error) {
      return (
        <PlanStateMessage data-testid="plan-billing-error">
          <FormattedMessage
            id="pixi_wizard.plan.error"
            defaultMessage="Couldn't load plans. Please try again."
          />
        </PlanStateMessage>
      );
    }
    if (!hasPlans) {
      return (
        <PlanStateMessage data-testid="plan-billing-empty">
          <FormattedMessage
            id="pixi_wizard.plan.empty"
            defaultMessage="Plans are unavailable right now. Please try again later."
          />
        </PlanStateMessage>
      );
    }

    if (isProTrialist) {
      return (
        <CardsRow>
          <PlanCard
            content={PLAN_CONTENT.pro}
            plan={proMonthly}
            billingCycle="month"
            cycleSuffix={monthlySuffix}
            selected={billingCycle === "month"}
            onSelect={() => selectProCycle("month")}
            data-testid="plan-card-pro-monthly"
          />
          <PlanCard
            content={PLAN_CONTENT.pro}
            plan={proAnnual}
            monthlyPlan={proMonthly}
            isFreeDevice={isFreeDevice}
            billingCycle="year"
            cycleSuffix={annualSuffix}
            selected={billingCycle === "year"}
            onSelect={() => selectProCycle("year")}
            data-testid="plan-card-pro-annual"
          />
        </CardsRow>
      );
    }

    return (
      <CardsRow>
        <PlanCard
          content={PLAN_CONTENT.core}
          plan={corePlan}
          monthlyPlan={coreMonthly}
          isFreeDevice={isFreeDevice}
          billingCycle={billingCycle}
          selected={planId === "core"}
          onSelect={() => selectPlan("core")}
          data-testid="plan-card-core"
        />
        <PlanCard
          content={PLAN_CONTENT.pro}
          plan={proPlan}
          monthlyPlan={proMonthly}
          isFreeDevice={isFreeDevice}
          billingCycle={billingCycle}
          selected={planId === "pro"}
          onSelect={() => selectPlan("pro")}
          data-testid="plan-card-pro"
        />
      </CardsRow>
    );
  };

  return (
    <StepLayout data-testid="plan-billing-step">
      <ChoiceColumn>
        <ChoiceHeader>
          <ChoiceTitle data-testid="plan-billing-title">
            <FormattedMessage
              id="pixi_wizard.plan.title"
              defaultMessage="Choose your plan"
            />
          </ChoiceTitle>
          {/* Currency is the trialist's own (Chargebee locks it) — not shown as
              a selector; it's already visible in the plan prices. Pro-trialists
              have no billing toggle either, so the row is omitted. */}
          {!isProTrialist && (
            <SelectorsRow>
              <SelectionPackaged
                data-testid="billing-selector"
                options={billingOptions}
                value={billingCycle}
                onChange={(value) => selectBillingCycle(value as BillingCycle)}
              />
            </SelectorsRow>
          )}
        </ChoiceHeader>

        {renderCards()}
      </ChoiceColumn>

      <ExclusiveDeals
        billingCycle={billingCycle}
        currency={currency}
        licenses={licenses}
        selectedDealId={effectiveDealId}
        shippingCountry={shippingCountry}
        shipOtherLabel={intl.formatMessage({
          id: "pixi_wizard.plan.deals.ship_other",
          defaultMessage: "Other",
        })}
        onSelectDeal={(dealId: DealId) => {
          updateState({ dealId });
          trackSelection({
            selectionType: "deal",
            selectionValue: dealId,
            nextDealId: dealId,
          });
        }}
        onChangeShippingCountry={(value) =>
          // An explicit "Ship to" pick overrides the country of a shipping
          // address already entered on the Payment step, so the two screens
          // never disagree (GRWBKK-1783) — a stored country would otherwise
          // sync itself back over this pick on re-entering step 3. Other
          // shipping-address fields are preserved; the FE-only "Other" option
          // is not a real country and is never written into the address.
          updateState({
            shippingCountry: value,
            ...(state.shippingAddress?.country && !isOtherShippingCountry(value)
              ? {
                  shippingAddress: { ...state.shippingAddress, country: value },
                }
              : {}),
          })
        }
      />
    </StepLayout>
  );
};

export const PlanBillingStep = injectIntl(PlanBillingStepComponent);
